/* Hero quote rotator — sections/home-hero.liquid.
 *
 * ES2019 only (FB/IG in-app WebViews) — no optional chaining / nullish
 * coalescing, and no `x != null ? x : y` either, because Shopify re-minifies
 * uploaded asset JS and rewrites that shape into `??`. See
 * scripts/validate-es2019.mjs.
 *
 * THE PASS IS BOUNDED, AND THAT IS THE FEATURE — the same rule, for the same
 * reason, as the top bar. Each page view performs exactly `quotes.length - 1`
 * transitions and then stops for good. Removing the bound turns the hero back
 * into perpetual auto-updating content with no way for a touch user to stop
 * it, which is the WCAG 2.2.2 Level A failure the bound exists to avoid. Read
 * docs/adr/0002-the-top-bar-stops-on-its-own.md before "fixing" the stop: the
 * argument there is about the bar but the criterion is about this too.
 *
 * SLOWER THAN THE BAR, DELIBERATELY. Ticket 10 required the hero to be the
 * calmest of the three moving surfaces on this page, so HOLD is 8s against the
 * bar's 5s and the fade is 900ms against its 420ms. If the bar's cadence ever
 * changes, this one still has to read as slower.
 *
 * NOTHING MOVES BEFORE LOAD. The quote sits inside the LCP region. Every
 * candidate is server-rendered and the first one is already opaque, so this
 * file only ever changes which one is showing — it is never on the path to
 * first paint, and it waits for `load` before arming so the rotation cannot
 * compete with the hero image for bandwidth or main thread.
 *
 * THE BOX CANNOT CHANGE HEIGHT, and that is CSS rather than JS: every quote
 * occupies the same grid cell under a two-line clamp, so the container is as
 * tall as its tallest member whichever one is visible. A rotator that moves
 * the hero's height was disqualified outright by ticket 10, not adjusted, so
 * if the clamp is ever removed this stops being safe to run.
 */
(function () {
  'use strict';

  var HOLD = 8000;
  var FADE = 900;

  var box = document.querySelector('[data-hero-quote]');
  if (!box) return;

  var quotes = box.querySelectorAll('.home-hero__q');
  if (quotes.length < 2) return;

  /* A still hero on the first quote. Unlike the bar, this one does not shuffle
     its starting point per page view: the first quote is the curated line when
     the merchant has left that on, and handing a reduced-motion visitor a
     different opening sentence than everyone else gets is not the trade the
     bar was making. */
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var idx = 0;
  var left = quotes.length - 1;
  var seen = false;
  var timer = null;
  var io = null;
  var armed = false;

  function halt() {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function tick() {
    if (timer || left <= 0 || !armed) return;
    if (!seen || document.hidden) return;
    timer = setInterval(advance, HOLD);
  }

  function done() {
    halt();
    if (io) {
      io.disconnect();
      io = null;
    }
  }

  function advance() {
    var prev = quotes[idx];
    var next = idx + 1;

    prev.className = 'home-hero__q';
    quotes[next].className = 'home-hero__q is-on';
    idx = next;

    /* The accessible name follows the visible quote at the midpoint of the
       cross-fade, so exactly one is ever exposed and it is always the one
       being looked at. No live region: a hero that announced itself every
       eight seconds would interrupt a screen reader for the whole visit. */
    setTimeout(function () {
      prev.setAttribute('aria-hidden', 'true');
      quotes[idx].removeAttribute('aria-hidden');
    }, FADE / 2);

    left -= 1;
    if (left <= 0) done();
  }

  function arm() {
    armed = true;
    tick();
  }

  if (typeof IntersectionObserver === 'function') {
    io = new IntersectionObserver(function (entries) {
      seen = entries[0].isIntersecting;
      if (seen) tick();
      else halt();
    });
    io.observe(box);
  } else {
    seen = true;
  }

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) halt();
    else tick();
  });

  if (document.readyState === 'complete') arm();
  else window.addEventListener('load', arm);
})();
